import { AirplaneLanding, AirplaneTakeOff, Traveler, MinusMath, PlusMath } from "@airhelp/icons";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { SectionHeader } from "modules/common/components";
import { AutocompleteInput } from "modules/common/components/AutocompleteForm/AutocompleteInput";
import { useState } from "react";
import { Container } from "components/Container";
import type { SbEditableComponentProps } from "types/storyblok";
import type { Airport } from "@airhelp/react";
import { ConsumerRights } from "@airhelp/react";
import type { CompensationCalculatorProps } from "./types";
import { Result } from "./components/Result";
import { Info } from "./components/Info";

export const CompensationCalculator = ({
  header,
  preheader,
  departurePlaceholder,
  destinationPlaceholder,
  passengerUnit,
  variant,
  successMessage,
  message,
  ctaButtonText,
  consumerRightsText,
  sbEditable,
}: SbEditableComponentProps<CompensationCalculatorProps>) => {
  const [departureAirport, setDepartureAirport] = useState<Airport>(null);
  const [arrivalAirport, setArrivalAirport] = useState<Airport>(null);
  const [numberOfPassengers, setNumberOfPassengers] = useState(1);
  const disabledAirports = [departureAirport, arrivalAirport];
  const minusButtonDisabled = numberOfPassengers <= 1;
  const plusButtonDisabled = numberOfPassengers === 1000;

  const handlePassengerIncrease = () => {
    if (plusButtonDisabled) return;
    setNumberOfPassengers((prev) => prev + 1);
  };

  const handlePassengerDecrease = () => {
    if (minusButtonDisabled) return;
    setNumberOfPassengers((prev) => prev - 1);
  };

  return (
    <Container
      mt={consumerRightsText ? -12 : 10}
      mb={12}
      px={4}
      display={{ base: "block", md: "none" }}
      {...sbEditable}
    >
      {consumerRightsText && <ConsumerRights text={consumerRightsText} mx={-4} w="auto" mb={10} />}
      <SectionHeader header={header} preheader={preheader} alignItems="flex-start" spacing={4} mb={8} />
      <Flex gap={4} direction="column">
        <AutocompleteInput
          placeholder={departurePlaceholder}
          setAirportSelected={setDepartureAirport}
          instanceId="departure-airport"
          icon={<AirplaneTakeOff boxSize={6} />}
          disabledAirports={disabledAirports}
        />
        <AutocompleteInput
          placeholder={destinationPlaceholder}
          setAirportSelected={setArrivalAirport}
          instanceId="destination-airport"
          disabledAirports={disabledAirports}
          icon={<AirplaneLanding boxSize={6} />}
        />

        <Flex gap={2} alignItems="center">
          <Traveler boxSize={6} color="greyscale.500" />
          <Text as="span" fontSize="md" fontWeight="medium">
            {`${numberOfPassengers} ${passengerUnit}`}
          </Text>
          <Button size="s" marginInlineStart="auto" onClick={handlePassengerDecrease} isDisabled={minusButtonDisabled}>
            <MinusMath />
          </Button>
          <Button size="s" onClick={handlePassengerIncrease} isDisabled={plusButtonDisabled}>
            <PlusMath />
          </Button>
        </Flex>
      </Flex>
      <Box bgColor="primary.100" mt={8} rounded="2xl">
        <Result
          departureAirport={departureAirport}
          arrivalAirport={arrivalAirport}
          numberOfPassengers={numberOfPassengers}
          message={message}
          successMessage={successMessage}
          ctaButtonText={ctaButtonText}
          variant={variant}
        />
        <Info />
      </Box>
    </Container>
  );
};
